/**
 * The member home-location CHANGE path (DNKADM-0239; DNKAPI-0329) — the ONE place the
 * home location is written, plus the display helper for the value it returns.
 *
 * Split out of `HomeLocationControl.tsx` by DNKADM-0249: a hook and a formatter living
 * beside a component break Fast Refresh (`react-refresh/only-export-components`). The
 * picker component that drives this hook stays in `HomeLocationControl.tsx` and imports
 * {@link HomeLocationOption} from here, so the dependency runs component → hook and never
 * back. Nothing about the write behavior changed in the move.
 *
 * What the home location drives (stated to the operator in the picker, at the point of
 * the decision): it sets which location's tax rates apply to this member's purchases, and
 * dink-api rejects a one-time sale before it charges when there is no effective home
 * (`ensureHomeForPurchase`, dink-api 2026-08-04) — so an unset home BLOCKS a purchase, it
 * does not merely leave it untaxed. "Effective home" includes the server's
 * single-active-location fallback (DNKADM-0239), so this bites a multi-location tenant.
 * The rule is the SERVER's: nothing here pre-blocks a sale. An unset home is an actionable
 * defect, marked in the member-detail header with a warning-toned **"Not set"** chip,
 * never a neutral blank (which would read as "no locations exist").
 *
 * Writes hit the bearer-only `PUT /v2/admin/members/{contactId}/home-location`
 * (`setMemberHomeLocation`); a `400 HOME_LOCATION_INVALID` (or any failure) surfaces
 * **inline in the open picker**, at the point of the action, keeps the dialog open, and
 * never clears the currently shown value. The hook reflects the server's authoritative
 * `{ locationId, name }` on success and raises a success toast. Server boundary (stated in
 * the helper copy so the operator is not surprised): changing a home does NOT re-tax an
 * already-active subscription.
 *
 * Not admin-gated, and deliberately so: changing a home location is available to every
 * operator who can view the member (only the GDPR "Delete member" beside it in the same
 * menu is admin-only — DNKADM-0247).
 */
import { useCallback, useState } from 'react';
import { useToast } from '@/components/useToast';
import { setMemberHomeLocation, type MemberHomeLocation } from '@/api/members';
import { toHomeLocationErrorMessage } from './membersError';

/** A picker option: a tenant-scoped ACTIVE location. */
export interface HomeLocationOption {
  id: string;
  name: string;
}

/** The home's best display name: the resolved name, falling back to the id. */
export function homeName(home: MemberHomeLocation): string {
  return home.name ?? home.locationId ?? '';
}

/**
 * The home-location change path: picker open/close state, the single
 * `setMemberHomeLocation` write, its inline error, and the success toast.
 *
 * The caller renders `<HomeLocationPickerDialog>` while `open` and reflects the
 * result through `onChanged`; it never calls the API itself. A failed write keeps
 * `open` true so the error stays visible in the picker.
 */
export function useHomeLocationChange({
  contactId,
  onChanged,
}: {
  contactId: string;
  /** Reflect a write's returned `{ locationId, name }` into the caller's view. */
  onChanged: (homeLocation: MemberHomeLocation | null) => void;
}) {
  const showToast = useToast();
  const [open, setOpenState] = useState(false);
  // A failed write surfaces inline (not toast-and-forget) so the operator can act
  // on it — shown in the open picker, at the point of the action.
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  // Opening starts clean: a previous attempt's failure never greets the next one.
  const setOpen = useCallback((next: boolean) => {
    if (next) setError(null);
    setOpenState(next);
  }, []);

  const submit = useCallback(
    async (option: HomeLocationOption) => {
      setError(null);
      setBusy(true);
      try {
        const result = await setMemberHomeLocation(contactId, option.id);
        // Prefer the server's authoritative view; fall back to the picked option.
        onChanged(result ?? { locationId: option.id, name: option.name });
        showToast(`Home location set: ${result?.name ?? option.name}.`);
        setOpenState(false);
      } catch (err) {
        // The picker stays OPEN with the error inline — with no card behind it there
        // is no "after it closes" surface. The shown value is untouched (the server
        // performed no upsert on a 400).
        setError(toHomeLocationErrorMessage(err));
      } finally {
        setBusy(false);
      }
    },
    [contactId, onChanged, showToast],
  );

  return { open, setOpen, busy, error, submit };
}
